import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { MarketingLayout, Section, Overline } from "@/marketing/MarketingLayout";
import { SERVICES, PLAN } from "@/marketing/content";
import { ArrowRight, CheckCircle2 } from "lucide-react";

export default function MarketingServices() {
  const navigate = useNavigate();
  return (
    <MarketingLayout>
      <Section className="pt-16 pb-4">
        <Overline>{SERVICES.hero.eyebrow}</Overline>
        <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter mb-5 max-w-3xl">{SERVICES.hero.headline}</h1>
        <p className="text-lg text-muted-foreground max-w-2xl">{SERVICES.hero.subheadline}</p>
      </Section>

      {SERVICES.services.map((svc, i) => (
        <Section key={svc.number} className="pt-14">
          <div className="grid lg:grid-cols-3 gap-8">
            <div>
              <div className="font-mono text-5xl font-black text-primary/25 mb-3">{svc.number}</div>
              <span className="text-xs font-mono uppercase tracking-wider text-primary">{svc.tag}</span>
              <h2 className="font-heading text-2xl font-bold tracking-tight mt-1 mb-3">{svc.headline}</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">{svc.description}</p>
            </div>
            <div className="lg:col-span-2 grid sm:grid-cols-2 gap-4">
              {svc.details.map((d) => (
                <div key={d.label} className="p-5 rounded-md border border-border bg-card" data-testid="service-detail">
                  <div className="flex items-center gap-2 mb-1.5">
                    <CheckCircle2 className="w-4 h-4 text-success" />
                    <span className="font-heading font-bold text-sm">{d.label}</span>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">{d.text}</p>
                </div>
              ))}
            </div>
          </div>
        </Section>
      ))}

      {/* Membership highlight */}
      <Section className="pt-20">
        <div className="rounded-md border border-primary/40 bg-card p-8 lg:p-10 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div>
            <Overline>One membership</Overline>
            <h2 className="font-heading text-2xl font-bold tracking-tight">{PLAN.name} — {PLAN.price} <span className="text-muted-foreground text-lg font-normal">/ {PLAN.period}</span></h2>
            <p className="text-sm text-muted-foreground mt-2 max-w-xl">{PLAN.tagline} {PLAN.guarantee}</p>
          </div>
          <Button size="lg" onClick={() => navigate("/register")} className="rounded-md gap-2 h-12 px-6 shrink-0" data-testid="services-register-cta">
            {PLAN.cta} <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </Section>

      {/* FAQ */}
      <Section className="pt-20">
        <Overline>FAQ</Overline>
        <h2 className="font-heading text-3xl font-bold tracking-tight mb-8">{SERVICES.faq.heading}</h2>
        <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
          {SERVICES.faq.items.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`}>
              <AccordionTrigger className="text-left font-medium">{f.question}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">{f.answer}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Section>
    </MarketingLayout>
  );
}
